(() => {
  const node = document.getElementById('OnyraPackagingConfig');
  if (!node || window.OnyraPackaging) return;
  const config = JSON.parse(node.textContent);
  const root = `${config.root || '/'}`.replace(/\/?$/, '/');
  let pending = null;
  const allowedForms = new WeakSet();

  const isPackaging = item => Number(item.product_id) === config.productId;
  const isMerchandise = item => !isPackaging(item) && !item.gift_card && Number(item.original_price ?? item.price) > 0;
  const eligibleCount = cart => (cart.items || []).reduce((total, item) => total + (isMerchandise(item) ? Number(item.quantity) || 0 : 0), 0);
  const promotionCount = eligibleCount;

  const request = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || config.errorMessage);
    return data;
  };

  const reconcile = async seed => {
    let cart = seed?.items ? seed : await request('cart.js');
    const packaging = cart.items.filter(isPackaging);
    const wanted = eligibleCount(cart) > 0;
    const usable = packaging.filter(item => Number(item.variant_id) === config.variantId);
    const keep = wanted ? usable[0] : null;
    const updates = {};
    for (const item of packaging) {
      const quantity = item === keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    }
    let changed = Object.keys(updates).length > 0;
    if (changed) cart = await request('cart/update.js', { updates });
    if (wanted && !keep) {
      await request('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties: { _onyra_packaging: 'included' } }] });
      cart = await request('cart.js');
      changed = true;
    }
    const finalPackaging = cart.items.filter(isPackaging);
    if (wanted && (finalPackaging.length !== 1 || finalPackaging[0].quantity !== 1 || Number(finalPackaging[0].final_price) !== 0)) {
      if (finalPackaging.length) {
        await request('cart/update.js', { updates: Object.fromEntries(finalPackaging.map(item => [item.key, 0])) });
      }
      throw new Error(config.errorMessage);
    }
    return { cart, changed };
  };

  const ensure = seed => {
    if (pending) return seed?.items ? pending.then(() => ensure()) : pending;
    pending = reconcile(seed).finally(() => { pending = null; });
    return pending;
  };

  const refresh = async () => {
    try {
      const result = await ensure();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: result.cart, onyraPackaging: true } }));
      if (result.changed) document.dispatchEvent(new CustomEvent('cart:build'));
    } catch (error) {
      console.warn('Onyra packaging:', error.message);
    }
  };

  // Grouping is by Shopify variant, active selling plan, and public options.
  // Private metadata and different discount allocations must not make a second
  // visible copy of the exact same merchandise in the drawer.
  const groupSignature = item => {
    const properties = Object.entries(item.properties || {})
      .filter(([name, value]) => !name.startsWith('_') && value != null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([
      Number(item.variant_id),
      item.selling_plan_allocation?.selling_plan?.id || null,
      properties
    ]);
  };

  const changeGroupQuantity = (key, quantity) => {
    const count = Math.max(0, Math.floor(Number(quantity)));
    if (!Number.isFinite(count)) return Promise.reject(new Error('Invalid quantity'));
    if (pending) return pending.catch(() => {}).then(() => changeGroupQuantity(key, count));

    pending = (async () => {
      const cart = await request('cart.js');
      const selected = cart.items.find(item => item.key === key);
      if (!selected || isPackaging(selected)) throw new Error('Cart line no longer available');
      const signature = groupSignature(selected);
      const group = cart.items.filter(item => !isPackaging(item) && groupSignature(item) === signature);
      const updates = Object.fromEntries(group.map(item => [item.key, item.key === key ? count : 0]));
      // One atomic cart update per click, instead of increasing one line and
      // leaving other lines from the same variant visible as duplicates.
      const updated = await request('cart/update.js', { updates });
      const result = await reconcile(updated);
      return { cart: result.cart, changed: true };
    })().finally(() => { pending = null; });
    return pending.then(result => result.cart);
  };

  window.OnyraPackaging = { ensure, eligibleCount, promotionCount, changeGroupQuantity };

  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-onyra-group-remove]');
    if (!link) return;
    event.preventDefault();
    if (link.dataset.onyraRemoving) return;
    link.dataset.onyraRemoving = 'true';
    try {
      const cart = await changeGroupQuantity(link.dataset.onyraGroupRemove, 0);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, onyraPackaging: true } }));
      document.dispatchEvent(new CustomEvent('cart:build'));
    } catch (error) {
      console.warn('Onyra cart remove:', error.message);
      document.dispatchEvent(new CustomEvent('cart:build'));
    } finally {
      delete link.dataset.onyraRemoving;
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('#CartDrawerForm, #CartPageForm')) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    if (allowedForms.has(form)) {
      allowedForms.delete(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.onyraPackagingBusy) return;
    form.dataset.onyraPackagingBusy = 'true';
    const button = event.submitter || form.querySelector('[name="checkout"]');
    if (button) button.disabled = true;
    const oldError = form.querySelector('[data-onyra-packaging-error]');
    if (oldError) oldError.remove();
    try {
      const { cart } = await ensure();
      if (!eligibleCount(cart)) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, onyraPackaging: true } }));
        document.dispatchEvent(new CustomEvent('cart:build'));
        return;
      }
      if (button) button.disabled = false;
      allowedForms.add(form);
      if (button) form.requestSubmit(button);
      else form.requestSubmit();
    } catch (error) {
      const message = document.createElement('p');
      message.dataset.onyraPackagingError = 'true';
      message.className = 'errors';
      message.setAttribute('role', 'alert');
      message.textContent = config.errorMessage;
      const wrapper = form.querySelector('.cart__checkout-wrapper');
      if (wrapper) wrapper.before(message);
      else form.prepend(message);
    } finally {
      if (button) button.disabled = false;
      delete form.dataset.onyraPackagingBusy;
    }
  }, true);

  document.addEventListener('cart:updated', event => {
    if (!event.detail?.onyraPackaging) void refresh();
  });
  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('pageshow', event => { if (event.persisted) void refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else void refresh();
})();
